/** Whether boot-time API-domain discovery has settled, and how. */
export type RuntimeStatus = 'pending' | 'ready' | 'unavailable'

type Listener = () => void

/**
 * Module-scope holder for the discovery status, kept alongside the adopted `RuntimeApi` (see
 * `runtime-api.ts`) but split out so React can subscribe to it via `useSyncExternalStore`.
 * Discovery is fire-and-forget from `main.tsx` and may settle before or after React mounts, so —
 * exactly like the deferred install prompt — the status lives here and components subscribe.
 */
let status: RuntimeStatus = 'pending'
const listeners = new Set<Listener>()

/** The current discovery status (drives the app's top-level view). */
export const getRuntimeStatus = (): RuntimeStatus => status

/** Record the settled discovery status and notify subscribers. */
export const setRuntimeStatus = (next: RuntimeStatus): void => {
  status = next
  listeners.forEach((notify) => notify())
}

/** Subscribe to status changes; returns an unsubscribe function. */
export const subscribeRuntimeStatus = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Reset to the initial pending state. For tests only — production sets status forward, never back. */
export const resetRuntimeStatus = (): void => {
  status = 'pending'
  listeners.clear()
}
